import { CAR_CONFIGS } from '../game/data/cars';
import { TRACK_CONFIGS } from '../game/data/tracks';
import { getSortedRegistryIds } from '../game/SelectionState';

interface LoginPayload {
  username: string;
  password: string;
}

interface StartScreenOptions {
  onLogin: (payload: LoginPayload) => Promise<void>;
  onRegister: (payload: LoginPayload) => Promise<void>;
}

interface CarSelectionOptions {
  selectedTrackId: number;
  selectedCarId: number;
  onCarSelect: (carId: number) => void;
  onBackToScores: () => void;
  onStartRace: () => void;
}

interface ScoreboardRow {
  name: string;
  carId: number;
  time: number;
  replayId: string | null;
  verificationStatus: 'legacy' | 'verified';
}

interface ScoreboardTab {
  trackId: number;
  label: string;
}

interface ScoreboardScreenOptions {
  tabs: ScoreboardTab[];
  activeTrackId: number;
  rows: ScoreboardRow[];
  isLoading?: boolean;
  errorText?: string;
  onTabSelect: (trackId: number) => void;
  onStart: () => void;
  onWatchReplay: (replayId: string) => void;
}

const TRACK_SCREEN_BY_ID: Record<number, string> = {
  1: '/assets/UI/TrackScreens/Track1 screen.jpg',
  2: '/assets/UI/TrackScreens/Track2 screen.jpg',
  3: '/assets/UI/TrackScreens/Track3 screen.jpg',
  4: '/assets/UI/TrackScreens/Track4 screen.jpg',
};

const CAR_SCREEN_BY_ID: Record<number, string> = {
  1: '/assets/UI/CarScreens/Car1 Screen.jpg',
  2: '/assets/UI/CarScreens/Car2 Screen.jpg',
  3: '/assets/UI/CarScreens/Car3 Screen.jpg',
};

const CAR_ICON_BY_ID: Record<number, string> = {
  1: '/assets/UI/CarIcons/Car1 icon.png',
  2: '/assets/UI/CarIcons/CarI2 icon.png',
  3: '/assets/UI/CarIcons/CarI3 icon.png',
};

function getFirstRegistryId(registry: Record<number, unknown>): number {
  return getSortedRegistryIds(registry)[0] ?? 1;
}

export class UIManager {
  constructor(private readonly root: HTMLElement) {}

  showBootScreen(): void {
    this.root.innerHTML =
      '<section class="ui-screen"><h2>Cars HTML5 Migration</h2><p>Booting game runtime...</p></section>';
  }

  showStartScreen(options: StartScreenOptions): void {
    const screen = document.createElement('section');
    screen.className = 'ui-screen ui-screen--fullscreen';
    screen.style.backgroundImage = "url('/assets/UI/StartScreen/StartScreen.png')";

    const logo = document.createElement('img');
    logo.src = '/assets/UI/StartScreen/Logo.jpg';
    logo.alt = 'Cars logo';
    logo.className = 'ui-start-logo';

    const panel = document.createElement('form');
    panel.className = 'ui-form-panel';

    const nameField = document.createElement('input');
    nameField.className = 'ui-input';
    nameField.placeholder = 'Username';
    nameField.autocomplete = 'username';

    const passField = document.createElement('input');
    passField.className = 'ui-input';
    passField.placeholder = 'Password';
    passField.type = 'password';
    passField.autocomplete = 'current-password';

    const login = document.createElement('button');
    login.className = 'ui-button';
    login.type = 'submit';
    login.value = 'login';
    login.textContent = 'Login';

    const register = document.createElement('button');
    register.className = 'ui-button ui-button--subtle';
    register.type = 'submit';
    register.value = 'register';
    register.textContent = 'Register';

    const buttons = document.createElement('div');
    buttons.className = 'ui-button-group';
    buttons.append(login, register);

    const help = document.createElement('p');
    help.className = 'ui-help';
    help.textContent = 'Log in to an existing account or create a new one.';

    panel.addEventListener('submit', async (event) => {
      event.preventDefault();
      const username = nameField.value.trim();
      const password = passField.value.trim();
      if (!username || !password) {
        help.classList.add('ui-help--error');
        help.textContent = 'Username and password are required.';
        return;
      }

      const submitter = (event as SubmitEvent).submitter;
      const action = submitter instanceof HTMLButtonElement ? submitter.value : 'login';

      login.disabled = true;
      register.disabled = true;
      nameField.disabled = true;
      passField.disabled = true;
      help.classList.remove('ui-help--error');
      help.textContent = action === 'register' ? 'Creating account...' : 'Logging in...';

      try {
        const authenticate = action === 'register' ? options.onRegister : options.onLogin;
        await authenticate({ username, password });
      } catch (error) {
        login.disabled = false;
        register.disabled = false;
        nameField.disabled = false;
        passField.disabled = false;
        help.classList.add('ui-help--error');
        help.textContent = error instanceof Error ? error.message : 'Login failed';
      }
    });

    panel.append(nameField, passField, buttons, help);
    screen.append(logo, panel);

    this.root.innerHTML = '';
    this.root.appendChild(screen);
  }

  showScoreboardScreen(options: ScoreboardScreenOptions): void {
    const screen = document.createElement('section');
    screen.className = 'ui-screen ui-screen--fullscreen';
    const fallbackTrackId = getFirstRegistryId(TRACK_CONFIGS);
    screen.style.backgroundImage = `url('${TRACK_SCREEN_BY_ID[options.activeTrackId] ?? TRACK_SCREEN_BY_ID[fallbackTrackId]}')`;

    const actions = document.createElement('div');
    actions.className = 'ui-actions';

    const title = document.createElement('h3');
    title.className = 'ui-title';
    title.textContent = 'Global Scoreboard';

    const startButton = document.createElement('button');
    startButton.className = 'ui-button';
    startButton.type = 'button';
    startButton.textContent = 'Start';
    startButton.addEventListener('click', () => options.onStart());

    actions.append(title, startButton);

    const tabs = document.createElement('div');
    tabs.className = 'ui-scoreboard-tabs';
    for (const tab of options.tabs) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ui-scoreboard-tab';
      if (tab.trackId === options.activeTrackId) {
        button.classList.add('is-active');
      }
      button.textContent = tab.label;
      button.addEventListener('click', () => options.onTabSelect(tab.trackId));
      tabs.appendChild(button);
    }

    const panel = document.createElement('div');
    panel.className = 'ui-scoreboard-panel';

    const note = document.createElement('p');
    note.className = `ui-help ${options.errorText ? 'ui-help--error' : ''}`;
    if (options.errorText) {
      note.textContent = options.errorText;
    } else if (options.isLoading) {
      note.textContent = 'Loading track results...';
    } else {
      note.textContent = `Best lap times for track ${options.activeTrackId}.`;
    }

    const table = document.createElement('table');
    table.className = 'ui-scoreboard-table';
    table.innerHTML =
      '<thead><tr><th>#</th><th>Name</th><th>Car</th><th>Time</th><th>Replay</th></tr></thead>';

    const tbody = document.createElement('tbody');
    options.rows.forEach((row, index) => {
      const tr = document.createElement('tr');
      const rank = document.createElement('td');
      rank.textContent = String(index + 1);
      const name = document.createElement('td');
      name.textContent = row.name;
      const car = document.createElement('td');
      car.textContent = String(row.carId);
      const time = document.createElement('td');
      time.textContent = (row.time / 1000).toFixed(3);
      const replay = document.createElement('td');
      const replayId = row.replayId;
      if (replayId !== null && row.verificationStatus === 'verified') {
        const watch = document.createElement('button');
        watch.type = 'button';
        watch.className = 'ui-button ui-button--subtle';
        watch.textContent = 'Watch';
        watch.addEventListener('click', () => options.onWatchReplay(replayId));
        replay.appendChild(watch);
      } else {
        replay.textContent =
          row.verificationStatus === 'verified' ? 'Unavailable' : 'Unverified / No replay';
      }
      tr.append(rank, name, car, time, replay);
      tbody.appendChild(tr);
    });

    if (!options.isLoading && options.rows.length === 0) {
      const tr = document.createElement('tr');
      tr.innerHTML = '<td colspan="5">No scores yet</td>';
      tbody.appendChild(tr);
    }

    table.appendChild(tbody);
    panel.append(tabs, note, table);
    screen.append(actions, panel);

    this.root.innerHTML = '';
    this.root.appendChild(screen);
  }

  showCarSelectionScreen(options: CarSelectionOptions): void {
    const screen = document.createElement('section');
    screen.className = 'ui-screen ui-screen--fullscreen';
    const fallbackCarId = getFirstRegistryId(CAR_CONFIGS);
    screen.style.backgroundImage = `url('${CAR_SCREEN_BY_ID[options.selectedCarId] ?? CAR_SCREEN_BY_ID[fallbackCarId]}')`;

    const actions = document.createElement('div');
    actions.className = 'ui-actions';

    const title = document.createElement('h3');
    title.className = 'ui-title';
    title.textContent = 'Select Car';

    const trackContext = document.createElement('span');
    trackContext.className = 'ui-context';
    trackContext.setAttribute('aria-label', `Current track: Track ${options.selectedTrackId}`);
    trackContext.textContent = `Track ${options.selectedTrackId}`;

    const buttons = document.createElement('div');
    buttons.className = 'ui-button-group';

    const back = document.createElement('button');
    back.className = 'ui-button ui-button--subtle';
    back.type = 'button';
    back.textContent = 'Back to Scores';
    back.addEventListener('click', () => options.onBackToScores());

    const start = document.createElement('button');
    start.className = 'ui-button';
    start.type = 'button';
    start.textContent = 'Start Race';
    start.addEventListener('click', () => options.onStartRace());

    buttons.append(back, start);
    actions.append(title, trackContext, buttons);

    const icons = document.createElement('div');
    icons.className = 'ui-icon-row';

    for (const carId of getSortedRegistryIds(CAR_CONFIGS)) {
      const iconButton = document.createElement('button');
      iconButton.type = 'button';
      iconButton.className = 'ui-icon-button';
      if (carId === options.selectedCarId) {
        iconButton.classList.add('is-selected');
      }
      iconButton.setAttribute('aria-label', `Select car ${carId}`);
      iconButton.addEventListener('click', () => options.onCarSelect(carId));

      const image = document.createElement('img');
      image.src = CAR_ICON_BY_ID[carId];
      image.alt = `Car ${carId}`;
      image.className = 'ui-icon-image';

      iconButton.appendChild(image);
      icons.appendChild(iconButton);
    }

    screen.append(actions, icons);
    this.root.innerHTML = '';
    this.root.appendChild(screen);
  }

  clear(): void {
    this.root.innerHTML = '';
  }
}
